/**
 * The kinds of tax document the profile knows about, and the wording each kind is shown and filed
 * under.
 *
 * In shared/ rather than in api/types/taxProfileExtraction/documentTypes.ts because both sides need
 * the same two things: the api names the OneDrive copy of a document after its kind, and the
 * dashboard shows the same wording next to the document. documentTypes.ts re-exports
 * OcrDocumentType, so every existing `types/taxProfileExtraction/documentTypes` import still works.
 */

export enum OcrDocumentType {
  FEDERAL_TAX_RETURN = 'FEDERAL_TAX_RETURN', // 1120, 1120-S, 1065
  SCHEDULE_K1 = 'SCHEDULE_K1',
  FEDERAL_TAX_PACKAGE = 'FEDERAL_TAX_PACKAGE', // FEDERAL_TAX_RETURN + SCHEDULE_K1 combined
  EIN_LETTER = 'EIN_LETTER', // CP 575, 147C
  FORM_2553 = 'FORM_2553',
  STATE_TAX_RETURN = 'STATE_TAX_RETURN',
  BANK_STATEMENT = 'BANK_STATEMENT',
  ARTICLES_OF_INCORPORATION = 'ARTICLES_OF_INCORPORATION',
  CAP_TABLE = 'CAP_TABLE',
  PARTNERSHIP_AGREEMENT = 'PARTNERSHIP_AGREEMENT',
  UNKNOWN = 'UNKNOWN',
}

/**
 * One label per kind: what a document of that kind is called, both in the file name the transfer
 * gives it in OneDrive ("Cap Table - 2026.pdf") and in the dashboard.
 *
 * Plain English rather than the enum name, and the same string in both places, so that a person
 * reading the folder and a person reading the dashboard see a document described the same way.
 * UNKNOWN is 'Document': a file whose kind nobody has decided yet still has to be called something.
 */
export const TAX_DOCUMENT_KIND_LABELS: Record<OcrDocumentType, string> = {
  [OcrDocumentType.FEDERAL_TAX_RETURN]: 'Federal Tax Return',
  [OcrDocumentType.SCHEDULE_K1]: 'Schedule K-1',
  [OcrDocumentType.FEDERAL_TAX_PACKAGE]: 'Federal Tax Package',
  [OcrDocumentType.EIN_LETTER]: 'EIN Letter',
  [OcrDocumentType.FORM_2553]: 'Form 2553',
  [OcrDocumentType.STATE_TAX_RETURN]: 'State Tax Return',
  [OcrDocumentType.BANK_STATEMENT]: 'Bank Statement',
  [OcrDocumentType.ARTICLES_OF_INCORPORATION]: 'Articles of Incorporation',
  [OcrDocumentType.CAP_TABLE]: 'Cap Table',
  [OcrDocumentType.PARTNERSHIP_AGREEMENT]: 'Partnership Agreement',
  [OcrDocumentType.UNKNOWN]: 'Document',
};

/**
 * True when a string is one of the document types above.
 *
 * The select in the Add document modal that names a ticked file's type carries an option that
 * stands for no type, and it asks this of what the select hands back rather than comparing that
 * option's value to the empty string, because the empty string is not what every environment
 * hands back for an option with no value of its own. Only one of these strings is a type.
 */
export const isOcrDocumentType = (value: string): value is OcrDocumentType =>
  (Object.values(OcrDocumentType) as string[]).includes(value);

/**
 * The vocabularies the profile's choice columns are stored in, one token each, canonical. The
 * api's validators build their `oneOf` checks from these lists (ENTITY_TYPES and
 * SPECIAL_RETURN_TYPES in api/types/taxProfileExtraction/documentTypes.ts), Typeform ingestion maps
 * the form's labels onto them, and the dashboard shows and edits them through the labels in
 * shared/constants/taxProfileFields.ts, so a value means the same thing whichever side wrote it.
 */
export const ENTITY_TYPE_VALUES = [
  'llc',
  'corporation',
  'partnership',
  'sole_proprietorship',
  'nonprofit',
  'other',
] as const;

/**
 * What k1_partners.entity_type holds: the boxes of Schedule K-1 (Form 1065) Part II item I1, "What
 * type of entity is this partner?", one token each.
 *
 * Its own list and not ENTITY_TYPE_VALUES above because the form offers kinds of partner a company
 * cannot be — an individual, a trust or estate, a retirement plan, an exempt organization. The api
 * builds the K-1 partner check from it (K1_PARTNER_ENTITY_TYPES in
 * api/types/taxProfileExtraction/documentTypes.ts) and the dashboard prints the labels below in the
 * Type column of the owners table, so a token means the same thing whichever side wrote it.
 */
export const K1_PARTNER_ENTITY_TYPE_VALUES = [
  'individual',
  'corporation',
  'partnership',
  'llc',
  'trust_estate',
  'disregarded_entity',
  'exempt_organization',
  'retirement_plan',
  'other',
] as const;

export type K1PartnerEntityType =
  (typeof K1_PARTNER_ENTITY_TYPE_VALUES)[number];

/**
 * How each of those tokens is written on screen. The stored form is a lowercase token, which is
 * what the value comparisons and the checks are written for; this is the only place it is turned
 * into what a person reads. A value that is not one of the tokens is printed as it is stored, so a
 * column filled before this list existed still shows what it holds.
 */
export const K1_PARTNER_ENTITY_TYPE_LABELS: Record<
  K1PartnerEntityType,
  string
> = {
  individual: 'Individual',
  corporation: 'Corporation',
  partnership: 'Partnership',
  llc: 'LLC',
  trust_estate: 'Trust or estate',
  disregarded_entity: 'Disregarded entity',
  exempt_organization: 'Exempt organization',
  retirement_plan: 'Retirement plan',
  other: 'Other',
};

/**
 * What a state return is a return for, one token each, stored in entity_state_tax_ids.tax_type. A
 * company registers separately per tax with most states, and the same state issues a different
 * number for each registration, which is why the tax type is part of what a state tax id row is
 * keyed by (stateTaxIdsPass in api/src/taxProfile/promotion/promoteExtraction.ts). The api builds
 * the check on the model's answer from it (STATE_TAX_TYPES in
 * api/types/taxProfileExtraction/documentTypes.ts) and the dashboard offers the labels below in the
 * select it edits the column with.
 *
 * 'pass_through_entity' is its own token rather than a kind of income tax: the pass-through entity
 * tax a state charges a partnership or an S corporation is a separate filing from the income tax
 * return, with its own registration.
 */
export const STATE_TAX_TYPE_VALUES = [
  'income',
  'franchise',
  'pass_through_entity',
  'sales_use',
  'withholding',
  'other',
] as const;

export type StateTaxType = (typeof STATE_TAX_TYPE_VALUES)[number];

/** How each of those tokens is written on screen. */
export const STATE_TAX_TYPE_LABELS: Record<StateTaxType, string> = {
  income: 'Income',
  franchise: 'Franchise',
  pass_through_entity: 'Pass-through entity',
  sales_use: 'Sales and use',
  withholding: 'Withholding',
  other: 'Other',
};

export const IRS_TAX_CLASSIFICATIONS = [
  'partnership',
  'c_corporation',
  's_corporation',
  'disregarded_entity',
  'nonprofit',
] as const;

export const SPECIAL_RETURN_TYPE_VALUES = [
  'initial',
  'final',
  'name_change',
  'address_change',
  'other',
] as const;

export const TAX_YEAR_TYPES = ['calendar', 'fiscal'] as const;

export const ACCOUNTING_METHODS = ['cash', 'accrual'] as const;

/**
 * How a Delaware entity's annual tax is worked out: the flat tax every LLC, LP, GP and LLP pays, or
 * one of the two methods a corporation's franchise tax can be computed by. Only 'flat_tax' is ever
 * derived by the application (api/src/taxProfile/delawareFranchiseTax.ts); the corporate methods
 * are a controller's choice.
 */
export const DE_FRANCHISE_TAX_METHODS = [
  'flat_tax',
  'authorized_shares',
  'assumed_par_value',
] as const;

/**
 * The file extensions a tax document may have, lowercased with the dot: the keys of
 * SUPPORTED_MIME_TYPES in api/src/services/taxProfileExtraction/classifier.ts, which is what the
 * pipeline refuses a file by. The dashboard's picker and upload refuse the same files before they
 * are copied anywhere; a test on the api side asserts the two lists are equal.
 *
 * The last two reach the model by a different route than the rest: a PDF or an image is sent as
 * bytes, and a .xlsx or .csv file is converted to CSV text first
 * (api/src/services/taxProfileExtraction/spreadsheetText.ts). Both are read, which is all this list
 * says.
 */
export const TAX_DOCUMENT_ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.tif',
  '.tiff',
  '.xlsx',
  '.csv',
] as const;

/**
 * The largest file the tax profile accepts, uploaded or read: 14 MiB. The reader sends the whole
 * file to the model in one request, base64-encoded (about a third larger) together with the prompt
 * and the response schema. A request carrying its file inline is limited to 20 MB in total
 * (Google's documented limit for inline data, not yet confirmed on Vertex), and 14 MiB comes to
 * about 18.7 MiB once encoded, leaving room for the prompt and the schema. The reader refuses a
 * file above this size as `too_large` (api/src/services/taxProfileExtraction/pipeline.ts). The Add
 * document modal refuses the same size before any bytes are sent, and the upload route refuses it
 * before storing anything: multer stops reading a file at this size, and a spreadsheet, which
 * multer reads up to this size, is refused by the route at TAX_DOCUMENT_MAX_SPREADSHEET_BYTES. So a
 * file that is accepted can always be read. A file picked from the client's team drive is refused
 * at the same size before it is copied, on the size Graph reports for it; the reader checks again
 * before and after its download.
 */
export const TAX_DOCUMENT_MAX_FILE_BYTES = 14 * 1024 * 1024;

/**
 * The largest spreadsheet (.xlsx or .csv) the tax profile accepts, uploaded or read: 3 MiB.
 *
 * The reader opens a .xlsx file with exceljs, which holds the whole workbook in memory as objects,
 * and that was measured at 60 to 200 times the file's size: a 1.9 MB workbook took about 167 MiB,
 * a 5 MB one about 350 MiB and an 18 MB one about 1.1 GiB, on api instances of 768 MB (production)
 * and 384 MB (test) that serve everybody else at the same time. A survey of the 414 spreadsheets in
 * production clients' External/Company Documents folders, where cap tables arrive, found the
 * largest at 2.63 MB and the 99th percentile at 0.60 MB, so 3 MiB refuses none of them.
 * taxDocumentMaxFileBytes below is the one place that says which limit a file falls under.
 */
export const TAX_DOCUMENT_MAX_SPREADSHEET_BYTES = 3 * 1024 * 1024;

/**
 * How many times a document is sent to the reader before the last failure is kept: three.
 *
 * api/src/services/taxProfileExtraction/pipeline.ts reads this into its MAX_ATTEMPTS and counts the
 * attempts on tax_document_index.extraction_attempts; the dashboard says "Attempt 2 of 3" under the
 * badge of a document being read again. The number is here so the two cannot come to say different
 * things. It must equal the Cloud Tasks queue's --max-attempts, for the reason pipeline.ts gives.
 */
export const TAX_DOCUMENT_MAX_READ_ATTEMPTS = 3;

/**
 * How many files one upload request may carry: one.
 *
 * multer's memory storage holds every part of a multipart body in the api process at once, and
 * uploadTaxDocuments then walks them one at a time while each one's Graph calls run, so the whole
 * body stays resident for the length of the request. One file per request means one request holds
 * at most TAX_DOCUMENT_MAX_FILE_BYTES. The Add document modal sends a batch as one request per
 * file, TAX_DOCUMENT_UPLOADS_AT_ONCE at a time (frontend/src/hooks/useTaxProfile.ts), so a batch
 * the page accepts is never one the route refuses.
 */
export const TAX_DOCUMENT_MAX_FILES = 1;

/**
 * File types the tax profile stores but never reads: the legacy Excel format and Word documents.
 *
 * Cap tables arrive as spreadsheets more often than not (11 of the 16 sampled on 2026-09-03), which
 * is why spreadsheets are now read rather than only filed: a .xlsx or .csv file is converted to CSV
 * text and the text goes to the model
 * (api/src/services/taxProfileExtraction/spreadsheetText.ts). The list below is what is left over.
 * exceljs does not read the old binary .xls format, and agreements arriving as Word files have no
 * conversion at all, so a document of one of these types is copied into both folders like any other
 * and shown as "On file"; the page says an export in a format we read can be read. Nothing sends
 * one to the reader.
 */
export const TAX_DOCUMENT_FILED_ONLY_EXTENSIONS = [
  '.xls',
  '.docx',
  '.doc',
] as const;

/** Every extension a controller may add, readable or filed only. */
export const TAX_DOCUMENT_UPLOAD_EXTENSIONS = [
  ...TAX_DOCUMENT_ACCEPTED_EXTENSIONS,
  ...TAX_DOCUMENT_FILED_ONLY_EXTENSIONS,
] as const;

const endsWithOneOf = (name: string, extensions: ReadonlyArray<string>) => {
  const lowered = name.toLowerCase();
  return extensions.some((extension) => lowered.endsWith(extension));
};

/** True when the reader can read a file of this name (a PDF, an image, or a .xlsx or .csv file). */
export const taxDocumentFileIsReadable = (name: string): boolean =>
  endsWithOneOf(name, TAX_DOCUMENT_ACCEPTED_EXTENSIONS);

/**
 * True when a file of this name is one of the two spreadsheet formats the reader converts to CSV
 * text. The dashboard asks this to say "Sheet 3" rather than "Page 3" next to a value read out of
 * one: the model is told to answer with the sheet's number in the same field a PDF's page number
 * goes in (see buildExtractionInstructions in api/src/services/taxProfileExtraction/schemaBuilder.ts), so
 * the file's name is the only thing that says which of the two a number means.
 */
export const taxDocumentFileIsSpreadsheet = (name: string): boolean =>
  endsWithOneOf(name, ['.xlsx', '.csv']);

/**
 * The largest file of this name the tax profile accepts: TAX_DOCUMENT_MAX_SPREADSHEET_BYTES for a
 * spreadsheet the reader converts (.xlsx or .csv), TAX_DOCUMENT_MAX_FILE_BYTES for everything else.
 * A .xls file is filed and never opened by exceljs, so it is held to the larger one.
 *
 * The Add document modal, the two add routes (api/routers/taxProfileRouter.ts and
 * api/src/taxProfile/dashboard/addDocuments.ts) and the reader
 * (api/src/services/taxProfileExtraction/pipeline.ts) all ask this, so the four cannot disagree
 * about a file.
 */
export const taxDocumentMaxFileBytes = (name: string): number =>
  taxDocumentFileIsSpreadsheet(name)
    ? TAX_DOCUMENT_MAX_SPREADSHEET_BYTES
    : TAX_DOCUMENT_MAX_FILE_BYTES;

/** True when a controller may add a file of this name: readable, or a .xls or Word file. */
export const taxDocumentFileCanBeAdded = (name: string): boolean =>
  endsWithOneOf(name, TAX_DOCUMENT_UPLOAD_EXTENSIONS);
